import { useQuery } from "@tanstack/react-query"
import { ChevronDown, ChevronUp, History, Plus, Trash2 } from "lucide-react"
import { DateTime } from "luxon"
import { useEffect, useMemo, useState } from "react"
import ReactMarkdown from "react-markdown"
import { Button } from "../components/ui/button"
import { Card, CardContent } from "../components/ui/card"
import { Checkbox } from "../components/ui/checkbox"
import { Input } from "../components/ui/input"
import { useExercises } from "../hooks/useExercises"
import { useSettings } from "../hooks/useSettings"
import { useRestTimerStore } from "../store"
import { apiFetch } from "../utils/apiFetch"
import type { TExercise, TExerciseSet } from "../utils/workoutUtils"
import ExercisesDropdown from "./ExercisesDropdown"
import PlateStack from "./PlateStack"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog"
import { Textarea } from "./ui/textarea"

interface ExerciseFormProps {
  exercise: TExercise
  onUpdate: (updatedExercise: TExercise) => void
  onDelete: (exerciseId: string) => void
  showCompleted?: boolean
  type: "workout" | "template"
  status: "idle" | "dragging"
}

/**
 * ExerciseForm component allows users to view and edit an exercise's details,
 */
export default function ExerciseForm({
  exercise,
  onUpdate,
  onDelete,
  showCompleted = false,
  type = "workout",
  status = "idle",
}: ExerciseFormProps) {
  const [expanded, setExpanded] = useState(true)
  const { data: exercises } = useExercises()
  const { data: settings } = useSettings()
  const [showHistoricalDialog, setShowHistoricalDialog] = useState<boolean>(false)
  const resetTimer = useRestTimerStore((s) => s.reset)
  const exerciseId = useMemo(() => {
    const found = exercises?.find((ex) => ex.name === exercise.name)
    return found ? found.exerciseId : null
  }, [exercise.name, exercises])
  /**
   * Handles changes to the exercise name.
   */
  const handleNameChange = (value: string) => {
    onUpdate({ ...exercise, name: value })
  }

  /**
   * Handles changes to a specific set's field (weight, reps, isCompleted).
   */
  const handleSetChange = (setId: string, field: keyof TExerciseSet, value: number | boolean) => {
    if (isNaN(Number(value))) {
      return
    }
    const updatedSets = exercise.sets.map((set: TExerciseSet) =>
      set.setId === setId ? { ...set, [field]: value } : set
    )
    if (field === "isCompleted" && value === true) {
      resetTimer()
    }
    onUpdate({ ...exercise, sets: updatedSets })
  }

  /**
   * Adds a new set to the exercise.
   */
  const addSet = () => {
    const lastSet =
      exercise.sets.length > 0
        ? exercise.sets[exercise.sets.length - 1]
        : { weight: 0, reps: 0, isCompleted: false, order: 0 }

    const newSet: TExerciseSet = {
      order: lastSet.order + 1,
      setId: crypto.randomUUID(),
      weight: lastSet.weight,
      reps: lastSet.reps,
      isCompleted: false,
    }

    onUpdate({ ...exercise, sets: [...exercise.sets, newSet] })
  }

  useEffect(() => {
    setExpanded(status === "dragging" ? false : true)
  }, [status])

  /**
   * Deletes a set from the exercise.
   */
  const deleteSet = (setId: string) => {
    const updatedSets = exercise.sets.filter((set: TExerciseSet) => set.setId !== setId)
    onUpdate({ ...exercise, sets: updatedSets })
  }

  const { data: historicalExercises, isLoading: isLoadingHistoricalExercises } = useQuery({
    queryKey: ["exerciseHistory", exerciseId],
    queryFn: async () => {
      const data = await apiFetch<
        Record<
          string,
          Array<{
            workoutName: string
            weight: number
            reps: number
            order: number
          }>
        >
      >(`/v1/exercises/${exerciseId}/history`, {
        method: "GET",
      })
      return data
    },
    enabled: !!exerciseId,
    staleTime: Infinity,
  })

  return (
    <Card className="workout-card mb-4">
      <div className={`flex w-full flex-col items-start justify-between gap-3 ${status === "dragging" ? "" : "p-4"}`}>
        <div className="flex w-full items-center space-x-2">
          {status !== "dragging" && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => {
                onUpdate({
                  ...exercise,
                  expanded: !exercise.expanded,
                })
              }}
            >
              {exercise?.expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </Button>
          )}
          <ExercisesDropdown value={exercise.name} onValueChange={handleNameChange} />
          {type === "workout" && (exercise.rpeMin || exercise.rpeMax) && (
            <span className="shrink-0 rounded-md bg-muted/40 px-2 py-0.5 text-muted-foreground text-xs">
              RPE {exercise.rpeMin ?? "?"}–{exercise.rpeMax ?? "?"}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-red-500 hover:bg-red-100/10 hover:text-red-700"
            onClick={() => onDelete(exercise.exerciseId ?? "")}
          >
            <Trash2 size={18} />
          </Button>
        </div>
      </div>

      {expanded && exercise?.expanded && (
        <div className="border-border border-t pt-3 pl-3">
          {type === "template" ? (
            <>
              <label htmlFor="instructions" className="mb-1 block text-muted-foreground text-sm">
                Instructions <span className="text-xs">(supports Markdown)</span>
              </label>
              <Textarea
                id="instructions"
                value={exercise.instructions || ""}
                onChange={(e) => onUpdate({ ...exercise, instructions: e.target.value })}
                placeholder="Any specific instructions for this exercise? (optional)"
                rows={2}
                className="w-full"
              />
              <div className="mt-2 flex items-center gap-3">
                <span className="text-muted-foreground text-sm">RPE target:</span>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={exercise.rpeMin ?? ""}
                    onChange={(e) => {
                      const val = e.target.value === "" ? null : Math.min(Math.max(Number(e.target.value), 1), 10)
                      onUpdate({ ...exercise, rpeMin: val })
                    }}
                    placeholder="Min"
                    className="workout-input w-16"
                  />
                  <span className="text-muted-foreground text-sm">–</span>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={exercise.rpeMax ?? ""}
                    onChange={(e) => {
                      const val = e.target.value === "" ? null : Math.min(Math.max(Number(e.target.value), 1), 10)
                      onUpdate({ ...exercise, rpeMax: val })
                    }}
                    placeholder="Max"
                    className="workout-input w-16"
                  />
                </div>
                <span className="text-muted-foreground text-xs">(1 = easy, 10 = failure)</span>
              </div>
            </>
          ) : (
            exercise.instructions && (
              <div className="prose prose-sm dark:prose-invert max-w-none pb-2">
                <ReactMarkdown>{exercise.instructions}</ReactMarkdown>
              </div>
            )
          )}

          <div className="mr-3 mb-2 flex items-center justify-end">
            {!isLoadingHistoricalExercises && (
              <Dialog
                open={showHistoricalDialog}
                onOpenChange={(open) => {
                  if (!open) {
                    setShowHistoricalDialog(false)
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowHistoricalDialog(true)
                    }}
                  >
                    <History className="inline h-4 w-4" />
                    <span>History</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>History: {exercise.name}</DialogTitle>
                  </DialogHeader>
                  <div className="mt-5 flex max-h-[75vh] flex-col gap-3 overflow-y-auto">
                    {Object.keys(historicalExercises || {}).length > 0 ? (
                      Object.entries(historicalExercises!).map(([date, sets]) => (
                        <Card key={date}>
                          <CardContent>
                            <div className="px-3 pb-3">
                              <h3>{DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}</h3>
                              {sets.length > 0 && <p>{sets[0]?.workoutName}</p>}
                              <div className="grid grid-cols-3 gap-2 text-sm">
                                {sets.map((set) => (
                                  <div key={set.order} className="rounded-md bg-muted/30 p-2">
                                    <div className="font-medium">Set {set.order}</div>
                                    <div className="text-muted-foreground">
                                      {set.weight} lbs × {set.reps} reps
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <p className="mt-2 text-sm">
                                Volume: {sets.reduce((acc, currValue) => acc + currValue.weight * currValue.reps, 0)}{" "}
                                lbs
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <p className="bg-muted/30 p-3">No historical data available.</p>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowHistoricalDialog(false)} className="w-full">
                      Close
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <CardContent className="p-2 pt-2 sm:p-4 sm:pt-2">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[320px]">
                <thead>
                  <tr>
                    <th className="pr-2 text-left font-medium text-muted-foreground text-xs sm:pr-4 sm:text-sm">Set</th>
                    <th className="pr-2 text-left font-medium text-muted-foreground text-xs sm:pr-4 sm:text-sm">
                      Weight
                    </th>
                    <th className="pr-2 text-left font-medium text-muted-foreground text-xs sm:pr-4 sm:text-sm">
                      Reps
                    </th>
                    {showCompleted && (
                      <th className="pr-2 text-left font-medium text-muted-foreground text-xs sm:pr-4 sm:text-sm">
                        Done
                      </th>
                    )}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {exercise.sets.map((set: TExerciseSet, index: number) => (
                    <tr key={index} className="border-border border-b last:border-0">
                      <td className="py-2 pr-2 text-sm sm:py-3 sm:pr-4">{index + 1}</td>
                      <td className="py-2 pr-2 sm:py-3 sm:pr-4">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-muted-foreground text-xs sm:h-8 sm:w-8"
                            onClick={() => handleSetChange(set.setId, "weight", Number(set.weight) - 5)}
                          >
                            -5
                          </Button>
                          <Input
                            type="text"
                            value={set.weight}
                            onChange={(e) => {
                              handleSetChange(set.setId, "weight", Number(e.target.value))
                            }}
                            className="workout-input w-14 sm:w-15"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-muted-foreground text-xs sm:h-8 sm:w-8"
                            onClick={() => handleSetChange(set.setId, "weight", Number(set.weight) + 5)}
                          >
                            +5
                          </Button>
                        </div>
                        {type === "workout" && settings?.showPlateCalculator !== false && (
                          <PlateStack targetWeight={Number(set.weight)} plateConfigs={settings?.plateConfig} />
                        )}
                      </td>
                      <td className="py-2 pr-2 sm:py-3 sm:pr-4">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-muted-foreground text-xs sm:h-8 sm:w-8"
                            onClick={() => handleSetChange(set.setId, "reps", Number(set.reps) - 1)}
                          >
                            -1
                          </Button>
                          <Input
                            type="text"
                            value={set.reps}
                            onChange={(e) => handleSetChange(set.setId, "reps", Number(e.target.value))}
                            className="workout-input w-10 sm:w-12"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-muted-foreground text-xs sm:h-8 sm:w-8"
                            onClick={() => handleSetChange(set.setId, "reps", Number(set.reps) + 1)}
                          >
                            +1
                          </Button>
                        </div>
                      </td>
                      {showCompleted && (
                        <td className="py-2 pr-2 sm:py-3 sm:pr-4">
                          <Checkbox
                            checked={set.isCompleted}
                            onCheckedChange={(checked: boolean) =>
                              handleSetChange(set.setId, "isCompleted", Boolean(checked))
                            }
                          />
                        </td>
                      )}
                      <td className="py-2 sm:py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-500 hover:bg-red-100/10 hover:text-red-700 sm:h-8 sm:w-8"
                          onClick={() => deleteSet(set.setId)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {exercise.sets.some((x) => x.isCompleted) && (
              <p className="mt-2 text-sm">
                Completed Volume:{" "}
                {exercise.sets
                  .filter((x) => x.isCompleted)
                  .reduce((acc, currValue) => acc + currValue.weight * currValue.reps, 0)}
              </p>
            )}
            <Button variant="outline" size="sm" className="mt-4" onClick={addSet} type="button">
              <Plus size={16} className="mr-2" />
              Add Set
            </Button>
          </CardContent>
        </div>
      )}
    </Card>
  )
}
